import React, { useEffect, useState } from "react";
import { PATHS } from "../../config/permissionsPaths";
import useAuth from "../../hooks/useAuth";
import Modal from "./Modal";
import { BtnGeneral } from "../../styles/Buttons";
import { toast } from "react-toastify";
import { phoneAPI } from "../../api/phoneApi";
import { usePatientData } from "../../context/PatientDataContext";
import icons from "../../assets/icons/iconMap";

const PhoneModal = ({ isOpen, onClose, autoid }) => {
  const [phones, setPhones] = useState([]);
  const [newPhone, setNewPhone] = useState("");
  const [isPrincipal, setIsPrincipal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const { hasPermission } = useAuth();
  const { fetchPatientData } = usePatientData();

  /** Cargar teléfonos */
  const getPhones = async () => {
    if (!autoid) return;
    try {
      const data = await phoneAPI.getPhones(autoid);
      setPhones(data || []);
    } catch (err) {
      console.error("Error al cargar teléfonos:", err);
    }
  };

  useEffect(() => {
    if (isOpen && autoid) getPhones();
  }, [isOpen, autoid]);

  /** Crear teléfono */
  const addPhone = async () => {
    if (saving) return;
    if (newPhone.length !== 10) {
      toast.error("El número de teléfono debe tener exactamente 10 caracteres.");
      return;
    }

    try {
      setSaving(true);
      const rs = await phoneAPI.savePhone({
        id: 0,
        autoid: autoid,
        telefono: newPhone,
        principal: isPrincipal ? 1 : 0,
      });

      if (!rs) return;

      setNewPhone("");
      setIsPrincipal(false);
      getPhones();
      fetchPatientData();
      onClose();
      toast.success("Teléfono guardado correctamente");
    } catch (err) {
      console.error("Error al guardar teléfono:", err);
    } finally {
      setSaving(false);
    }
  };

  /** Editar teléfono */
  const updatePhone = async () => {
    if (saving) return;
    if (newPhone.length !== 10) {
      toast.error("El número de teléfono debe tener exactamente 10 caracteres.");
      return;
    }

    try {
      setSaving(true);
      const rs = await phoneAPI.savePhone({
        id: editingId,
        autoid: autoid,
        telefono: newPhone,
        principal: isPrincipal ? 1 : 0,
      });

      if (!rs) return;

      setNewPhone("");
      setIsPrincipal(false);
      setEditingId(null);
      getPhones();
      fetchPatientData();
      toast.success("Teléfono actualizado correctamente");
    } catch (err) {
      console.error("Error al actualizar teléfono:", err);
    } finally {
      setSaving(false);
    }
  };

  /** Cargar datos para editar */
  const loadPhoneForEdit = (phone) => {
    setNewPhone(phone.telefono);
    setIsPrincipal(String(phone.principal) === "1");
    setEditingId(phone.id);
  };

  /** Cancelar edición */
  const cancelEdit = () => {
    setNewPhone("");
    setIsPrincipal(false);
    setEditingId(null);
  };

  /** Inactivar teléfono */
  const inactivePhone = async (phoneId) => {
    try {
      const data = await phoneAPI.inactivePhone(phoneId);

      if (!data) {
        toast.error("Error al eliminar el numero de telefono");
        return;
      }

      toast.success("Teléfono eliminado correctamente");
      getPhones();
      fetchPatientData();
    } catch (err) {
      console.error("Error al eliminar teléfono:", err);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Teléfonos del Paciente" width="400px">
      <>
        {hasPermission(PATHS.CREAR_TELEFONO) && (
          <>
            {/* Input teléfono */}
            <input
              type="text"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value.replace(/\D/g, ""))}
              maxLength={10}
              minLength={10}
              placeholder="Ingrese 10 dígitos"
              style={{
                width: "100%",
                padding: "3px 15px",
                marginBottom: "10px",
                border: "1px solid #ccc",
                borderRadius: "8px",
                fontSize: "0.95rem",
              }}
            />

            <label style={{ display: "block", marginBottom: "10px" }}>
              <input
                type="checkbox"
                checked={isPrincipal}
                onChange={(e) => setIsPrincipal(e.target.checked)}
              />
              {" "} ¿Es principal?
            </label>

            <div style={{ display: "flex", gap: "8px" }}>
              <BtnGeneral
                bg={editingId ? "blue" : "green"}
                padding="7px"
                radius="8px"
                disabled={saving}
                onClick={editingId ? updatePhone : addPhone}
              >
                {saving ? "Guardando..." : editingId ? "Actualizar" : "Guardar"}
              </BtnGeneral>

              {editingId && (
                <BtnGeneral
                  bg="gray"
                  padding="7px"
                  radius="8px"
                  disabled={saving}
                  onClick={cancelEdit}
                >
                  Cancelar
                </BtnGeneral>
              )}
            </div>

            <hr style={{ margin: "15px 0" }} />
          </>
        )}

        {/* Teléfonos existentes */}
        <h5 style={{ marginBottom: "5px", color: "#444" }}>Números registrados:</h5>

        {phones.length > 0 ? (
          <ul style={{ listStyle: "none", padding: 0 }}>
            {phones.map((p, i) => (
              <li
                key={i}
                style={{
                  // marginBottom: "8px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "4px 16px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  backgroundColor: editingId === p.id ? "#e3f2fd" : "transparent",
                }}
              >
                <span>
                  {p.telefono} {p.principal === "1" ? "(Principal)" : ""}
                </span>

                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                {hasPermission(PATHS.CREAR_TELEFONO) && (
                  <img
                    src={icons["pencil"]}
                    alt="Editar Telefono"
                    title="Editar Telefono"
                    style={{
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      width: 16,
                      height: 16,
                    }}
                    className="hover"
                    onClick={() => loadPhoneForEdit(p)}
                  />
                )}

                {hasPermission(PATHS.ELIMINAR_TELEFONO) && (
                  <button
                    onClick={() => inactivePhone(p.id)}
                    style={{
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      fontSize: "16px",
                      padding: 0,
                      lineHeight: 1,
                    }}
                    className="hover"
                    title="Eliminar"
                  >
                    ✕
                  </button>
                )}
              </div>

              </li>
            ))}
          </ul>
        ) : (
          <p>No hay teléfonos registrados.</p>
        )}
      </>
    </Modal>
  );
};

export default PhoneModal;
